import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, CornerDownLeft, Plus, X } from '@/components/icons';
import { Tooltip } from '@/components/ui/beui';
import { EASE_OUT, SPRING_PANEL } from '@/components/ui/beui/lib/ease';
import { POPULAR_ICONS } from '../../constants';
import { IconData } from '../../types';
import { TechIcon, parseIconId } from '../../lib/icons';
import { listCustomIcons, addCustomIcon, removeCustomIcon } from '../../services/customIconLibrary';
import { CustomIconPanel } from '../CustomIconPanel';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (icon: IconData) => void;
  isDark: boolean;
}

const RECENT_KEY = 'ss:recent-icons';
const MAX_RECENT = 8;
const MAX_RESULTS = 48;

// Category display order + labels (POPULAR_ICONS carry a `category`).
const CATEGORY_ORDER = [
  'frontend',
  'backend',
  'database',
  'cloud',
  'devops',
  'infrastructure',
  'monitoring',
  'security',
  'storage',
  'network',
  'brands',
];
const CATEGORY_LABEL: Record<string, string> = {
  frontend: 'Frontend',
  backend: 'Backend',
  database: 'Database',
  cloud: 'Cloud',
  devops: 'DevOps',
  infrastructure: 'Infrastructure',
  monitoring: 'Monitoring',
  security: 'Security',
  storage: 'Storage',
  network: 'Network',
  brands: 'Brands',
};

interface Group {
  key: string;
  label: string;
  items: IconData[];
  start: number; // absolute index of the first item in the flat list
}

// Subsequence match: are all of `q`'s chars found in `s` in order?
function isSubsequence(q: string, s: string): boolean {
  let i = 0;
  for (let j = 0; j < s.length && i < q.length; j++) {
    if (s[j] === q[i]) i++;
  }
  return i === q.length;
}

// Relevance score for an icon against a lowercased query (0 = no match).
function scoreIcon(icon: IconData, q: string): number {
  const title = icon.title.toLowerCase();
  const name = parseIconId(icon.icon).name.toLowerCase();
  const cat = (icon.category || '').toLowerCase();

  if (title === q) return 1000;
  if (title.startsWith(q)) return 850 - title.length;
  if (name.startsWith(q)) return 750 - name.length;
  if (title.split(/[\s\-_.]+/).some((w) => w.startsWith(q))) return 620;
  if (title.includes(q)) return 480;
  if (name.includes(q)) return 360;
  if (isSubsequence(q, title)) return 240;
  if (isSubsequence(q, name)) return 180;
  if (cat.includes(q)) return 90;
  return 0;
}

function readRecent(): IconData[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const ids: string[] = JSON.parse(raw);
    return ids
      .map((id) => POPULAR_ICONS.find((i) => i.icon === id))
      .filter((i): i is IconData => Boolean(i))
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function pushRecent(icon: IconData) {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    const next = [icon.icon, ...ids.filter((id) => id !== icon.icon)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, onSelect, isDark }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<IconData[]>([]);
  const [customIcons, setCustomIcons] = useState<IconData[]>([]);
  const [view, setView] = useState<'search' | 'custom'>('search');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const keyboardNav = useRef(false);

  // Focus + reset on open; refresh the recent + custom-icon lists.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setActive(0);
    setView('search');
    setRecent(readRecent());
    listCustomIcons().then((icons) =>
      setCustomIcons(icons.map((c) => ({ title: c.name, icon: c.url, hex: '64748b' })))
    );
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Commit a custom node (uploaded R2 url or a pasted image url) AND save it to the
  // reuse library so it shows under "My Nodes" next time. The name becomes both the
  // library label and the node's on-canvas label.
  const commitCustom = useCallback(
    (iconId: string, name?: string) => {
      const label = name?.trim() || 'Custom node';
      void addCustomIcon({ url: iconId, name: label }).catch(() => {});
      onSelect({ title: label, icon: iconId, hex: '64748b' });
      onClose();
    },
    [onSelect, onClose]
  );

  // Remove a custom icon from the library (keyed by url; the R2 object is left in place).
  const handleRemoveCustom = useCallback(async (url: string) => {
    await removeCustomIcon(url);
    setCustomIcons((prev) => prev.filter((c) => c.icon !== url));
  }, []);

  // Build grouped results + a flat list (for keyboard navigation).
  const { groups, flat } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const built: Group[] = [];
    const flatItems: IconData[] = [];

    const addGroup = (key: string, label: string, items: IconData[]) => {
      if (items.length === 0) return;
      built.push({ key, label, items, start: flatItems.length });
      flatItems.push(...items);
    };

    if (!q) {
      // Browse: recent + the user's custom icons + a curated popular set.
      if (recent.length) addGroup('recent', 'Recent', recent);
      if (customIcons.length) addGroup('mycustom', 'My Nodes', customIcons);
      const recentIds = new Set(recent.map((r) => r.icon));
      addGroup(
        'popular',
        'Popular',
        POPULAR_ICONS.filter((i) => !recentIds.has(i.icon)).slice(0, 24)
      );
    } else {
      // The user's matching custom icons rank first.
      const customMatches = customIcons.filter((c) => c.title.toLowerCase().includes(q));
      if (customMatches.length) addGroup('mycustom', 'My Nodes', customMatches);

      // Search: score, dedupe by title, cap, then group by category.
      const seen = new Set<string>();
      const scored = POPULAR_ICONS.map((icon) => ({ icon, score: scoreIcon(icon, q) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .filter(({ icon }) => {
          if (seen.has(icon.title)) return false;
          seen.add(icon.title);
          return true;
        })
        .slice(0, MAX_RESULTS);

      const byCat = new Map<string, IconData[]>();
      for (const { icon } of scored) {
        const cat = icon.category || 'brands';
        (byCat.get(cat) ?? byCat.set(cat, []).get(cat)!).push(icon);
      }
      // Order categories by their best-scoring member, ties broken by CATEGORY_ORDER.
      const bestScore = new Map<string, number>();
      for (const { icon, score } of scored) {
        const cat = icon.category || 'brands';
        if (!bestScore.has(cat)) bestScore.set(cat, score);
      }
      const cats = [...byCat.keys()].sort((a, b) => {
        const d = (bestScore.get(b) ?? 0) - (bestScore.get(a) ?? 0);
        if (d !== 0) return d;
        return CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b);
      });
      for (const cat of cats) {
        addGroup(cat, CATEGORY_LABEL[cat] || cat, byCat.get(cat)!);
      }
    }

    return { groups: built, flat: flatItems };
  }, [query, recent, customIcons]);

  // Keep the active index in range whenever results change.
  useEffect(() => {
    setActive((a) => (flat.length === 0 ? 0 : Math.min(a, flat.length - 1)));
  }, [flat.length]);

  const commit = useCallback(
    (icon: IconData) => {
      pushRecent(icon);
      onSelect(icon);
      onClose();
    },
    [onSelect, onClose]
  );

  // Keyboard navigation across the (grouped) grid via the flat index. Only in the
  // search view — the custom-icon panel has its own inputs.
  useEffect(() => {
    if (!isOpen || view !== 'search') return;
    const onKey = (e: KeyboardEvent) => {
      if (flat.length === 0) {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
        return;
      }
      const clamp = (n: number) => Math.max(0, Math.min(flat.length - 1, n));
      // Up/Down only, so Left/Right stay free for the search input's cursor.
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        keyboardNav.current = true;
        setActive((a) => clamp(a + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        keyboardNav.current = true;
        setActive((a) => clamp(a - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const icon = flat[active];
        if (icon) commit(icon);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, view, flat, active, commit, onClose]);

  // Scroll the active tile into view on keyboard navigation.
  useEffect(() => {
    if (!isOpen || !keyboardNav.current) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-flat="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
    keyboardNav.current = false;
  }, [active, isOpen]);

  const iconColor = isDark ? 'e2e8f0' : '334155';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[12vh] backdrop-blur-sm"
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 6 }}
            transition={{ ...SPRING_PANEL, opacity: { duration: 0.16, ease: EASE_OUT } }}
            className="flex w-full max-w-[600px] flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Add a node"
          >
          {view === 'custom' ? (
          <CustomIconPanel onBack={() => setView('search')} onAdd={commitCustom} />
        ) : (
        <>
        {/* Search */}
        <div className="flex items-center gap-2.5 px-4">
          <Search className="h-4 w-4 flex-none text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Search technologies…"
            className="h-12 w-full bg-transparent text-[14px] outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[420px] overflow-y-auto p-1.5">
          {flat.length === 0 ? (
            <div className="px-3 py-10 text-center text-[13px] text-muted-foreground">
              No technologies match “{query}”
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="mb-1">
                <div className="px-2 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                  {group.label}
                </div>
                {group.items.map((icon, i) => {
                  const idx = group.start + i;
                  const isActive = idx === active;
                  const isCustom = group.key === 'mycustom';
                  const inner = (
                    <>
                      <span className="flex h-6 w-6 flex-none items-center justify-center rounded border border-border bg-card">
                        <TechIcon icon={icon.icon} color={iconColor} size={15} alt="" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[13px]">{icon.title}</span>
                    </>
                  );

                  // Custom-library rows get a hover remove button, so they need a
                  // wrapping row (can't nest a button in a button).
                  if (isCustom) {
                    return (
                      <div
                        key={`${icon.icon}-${idx}`}
                        data-flat={idx}
                        onMouseMove={() => {
                          keyboardNav.current = false;
                          setActive(idx);
                        }}
                        className={`group flex items-center rounded-md pr-1 ${
                          isActive ? 'bg-muted text-foreground' : 'text-foreground hover:bg-muted/60'
                        }`}
                      >
                        <Tooltip content={icon.title} wrapperClassName="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => commit(icon)}
                            className="flex w-full min-w-0 items-center gap-2.5 px-2.5 py-2 text-left"
                            aria-label={icon.title}
                          >
                            {inner}
                          </button>
                        </Tooltip>
                        <button
                          type="button"
                          onClick={() => handleRemoveCustom(icon.icon)}
                          aria-label={`Remove ${icon.title} from your nodes`}
                          className="flex h-6 w-6 flex-none items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-foreground/10 hover:text-foreground group-hover:opacity-100"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  }

                  return (
                    <Tooltip
                      key={`${icon.icon}-${idx}`}
                      content={icon.title}
                      wrapperClassName="flex"
                    >
                      <button
                        type="button"
                        data-flat={idx}
                        onMouseMove={() => {
                          keyboardNav.current = false;
                          setActive(idx);
                        }}
                        onClick={() => commit(icon)}
                        className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left ${
                          isActive ? 'bg-muted text-foreground' : 'text-foreground hover:bg-muted/60'
                        }`}
                        aria-label={icon.title}
                      >
                        {inner}
                        {isActive && <CornerDownLeft className="h-3.5 w-3.5 flex-none opacity-40" />}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer: custom icon */}
        <div className="border-t border-border p-1.5">
          <button
            type="button"
            onClick={() => setView('custom')}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          >
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded border border-dashed border-border">
              <Plus className="h-3.5 w-3.5" />
            </span>
            Add a custom node…
          </button>
        </div>
        </>
        )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
